import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Container } from '@mui/material';
import { apiService } from '../services/apiService';
import { markOnboardingPendingForNextLogin } from '../utils/onboardingStorage';
import { redirectToTenant } from '../utils/tenantResolver';

const POLL_MS = 2500;
const MAX_ATTEMPTS = 48;

const PENDING_EMAIL_KEY = 'pendingSignupVerifyEmail';

/**
 * Shown after Stripe redirects to /success. Polls subscription until ACTIVE, then sends user to login.
 */
const SignupSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('Processing payment…');
  const [error, setError] = useState<string | null>(null);
  const attempts = useRef(0);
  const intervalRef = useRef<number | null>(null);
  const activationTriggered = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const email =
      sessionStorage.getItem(PENDING_EMAIL_KEY) ||
      params.get('email') ||
      '';

    const normalized = email.trim().toLowerCase();

    const stop = () => {
      if (intervalRef.current != null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const redirectToLogin = (organizationSlug?: string | null) => {
      try {
        sessionStorage.removeItem(PENDING_EMAIL_KEY);
      } catch {
        /* ignore */
      }
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('currentOrganization');
      } catch {
        /* ignore */
      }
      setMessage('Your subscription is active. Redirecting to sign in…');
      markOnboardingPendingForNextLogin();
      const loginPath = `/login?onboarding=1&returnUrl=${encodeURIComponent('/proxy-settings')}`;
      window.setTimeout(() => {
        if (organizationSlug) {
          redirectToTenant(organizationSlug, loginPath);
          return;
        }
        navigate(loginPath, { replace: true });
      }, 1200);
    };

    const triggerActivation = async () => {
      if (!sessionId || activationTriggered.current) return;
      activationTriggered.current = true;
      try {
        const result = await apiService.verifyPayment(sessionId);
        if (result && !result.error && result.message) {
          redirectToLogin(result.organizationSlug);
          return true;
        }
      } catch {
        // Activation via session_id failed; fall through to email polling
      }
      return false;
    };

    const startPolling = () => {
      if (!normalized) {
        if (!sessionId) {
          setError('Missing email for verification. Return to signup and try again, or open this page from the same browser you used to pay.');
          setMessage('');
        }
        return;
      }

      const tick = async () => {
        try {
          if (sessionId) {
            try {
              const sessionResult = await apiService.verifyPayment(sessionId);
              if (sessionResult && !sessionResult.error && sessionResult.message) {
                stop();
                redirectToLogin(sessionResult.organizationSlug);
                return;
              }
            } catch {
              /* session verify can fail until backend is ready; continue to email check */
            }
          }
          const { status } = await apiService.verifySubscriptionByEmail(normalized);
          if (status === 'ACTIVE') {
            stop();
            redirectToLogin();
            return;
          }
          attempts.current += 1;
          if (attempts.current >= MAX_ATTEMPTS) {
            stop();
            setError(
              'We could not confirm your subscription yet. If you completed payment, wait a few minutes and try signing in from the login page.'
            );
            setMessage('');
            return;
          }
          setMessage('Processing payment…');
        } catch (e: any) {
          attempts.current += 1;
          if (attempts.current >= MAX_ATTEMPTS) {
            stop();
            setError(e.response?.data?.message || e.message || 'Verification failed.');
            setMessage('');
            return;
          }
          setMessage('Processing payment…');
        }
      };

      void tick();
      intervalRef.current = window.setInterval(() => void tick(), POLL_MS);
    };

    const init = async () => {
      const activated = await triggerActivation();
      if (!activated) {
        startPolling();
      }
    };

    void init();
    return () => stop();
  }, [navigate]);

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box textAlign="center">
        <Typography variant="h5" gutterBottom>
          Payment status
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
        {!error && message && (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={32} />
            <Typography color="text.secondary">{message}</Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SignupSuccess;
